'use client';

import { useEffect, useState } from 'react';

import { getENV } from '../../../utils';

const SDK_LOAD_TIMEOUT_MS = 10_000;

type FormState = 'loading' | 'ready' | 'error';

type UseMedalliaFormParams = {
    isActive?: boolean;
};

export function useMedalliaForm({ isActive = true }: UseMedalliaFormParams = {}) {
    const { NEXT_PUBLIC_MEDALLIA_FORM_ID_US, NEXT_PUBLIC_MEDALLIA_FORM_ID_CA, NEXT_PUBLIC_LOCALE } = getENV();
    const isCA = NEXT_PUBLIC_LOCALE === 'en-CA';
    const formId = isCA ? NEXT_PUBLIC_MEDALLIA_FORM_ID_CA : NEXT_PUBLIC_MEDALLIA_FORM_ID_US;

    const [formState, setFormState] = useState<FormState>('loading');

    const loadFeedbackForm = () => {
        if (!window.KAMPYLE_ONSITE_SDK) {
            setFormState('error');
            return;
        }

        const loadedForm = window.KAMPYLE_ONSITE_SDK.loadForm(formId);
        setFormState(loadedForm ? 'ready' : 'error');
    };

    useEffect(() => {
        if (!isActive) return;

        if (window.KAMPYLE_ONSITE_SDK) {
            loadFeedbackForm();
            return;
        }

        const handleSDKReady = () => {
            clearTimeout(timeoutId);
            loadFeedbackForm();
        };

        const handleSDKTimeout = () => {
            window.removeEventListener('neb_OnsiteLoaded', handleSDKReady);
            setFormState('error');
        };

        const timeoutId = setTimeout(handleSDKTimeout, SDK_LOAD_TIMEOUT_MS);
        window.addEventListener('neb_OnsiteLoaded', handleSDKReady, { once: true });

        return () => {
            window.removeEventListener('neb_OnsiteLoaded', handleSDKReady);
            clearTimeout(timeoutId);
        };
    }, [isActive, formId]);

    const onOpen = () => {
        if (!isActive) return;

        if (!window.KAMPYLE_ONSITE_SDK) {
            setFormState('error');
            return;
        }

        const openedForm = window.KAMPYLE_ONSITE_SDK.showForm(formId);
        if (!openedForm) {
            setFormState('error');
        }
    };

    return { formState, onOpen };
}
